import { ChevronDown, ChevronRight, Loader } from 'lucide-react';

import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

interface RowExpandToggleProps {
  canExpand: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  onToggle: () => void;
}

/**
 * Expand/collapse chevron used by the project and member rows of the approvals
 * tree. Shows a spinner while the row's children are lazy-loading and stays
 * (invisibly) in the layout when a row can't expand, so names align across rows.
 */
export function RowExpandToggle({
  canExpand,
  isExpanded,
  isLoading,
  onToggle,
}: RowExpandToggleProps) {
  return (
    <Button
      variant="ghost"
      size="xxs"
      // Icon-only control: name it for screen readers (and for stable test selectors).
      aria-label={i18n.t(isExpanded ? 'common:collapseRow' : 'common:expandRow')}
      aria-expanded={canExpand ? isExpanded : undefined}
      // A leaf row keeps the toggle only as a layout spacer (`invisible` below) — take it out
      // of the a11y tree and the tab order so it isn't announced as a phantom control.
      aria-hidden={canExpand ? undefined : true}
      tabIndex={canExpand ? undefined : -1}
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn('shrink-0 text-muted-foreground', !canExpand && 'invisible')}
    >
      {isLoading ? (
        <Loader size={16} className="animate-spin" />
      ) : isExpanded ? (
        <ChevronDown size={16} />
      ) : (
        <ChevronRight size={16} />
      )}
    </Button>
  );
}
